import React from "react";
import AssignmentChip from "./AssignmentChip";
import { sortAssignments } from "../../../utils/scheduleTimeUtils";

function SectionRow({
  iso,
  row,
  employeeById,
  readOnly,
  dragOverKey,
  rowDragType,
}) {
  const dragOver = dragOverKey === `${iso}__${row.roleKey}`;

  function getReorderSourceRoleKey(e) {
    try {
      return e.dataTransfer.getData(rowDragType) || "";
    } catch {
      return "";
    }
  }

  function isReorderDrag(e) {
    try {
      const types = Array.from(e.dataTransfer?.types || []);
      return types.includes(rowDragType);
    } catch {
      return false;
    }
  }

  function handleRowDragStart(e) {
    if (!row.isExtraSlot || readOnly) return;
    try {
      e.dataTransfer.setData(rowDragType, row.roleKey);
      e.dataTransfer.effectAllowed = "move";
    } catch {
      // ignore
    }
  }

  function handleRowDrop(e) {
    const sourceRoleKey = getReorderSourceRoleKey(e);
    if (!sourceRoleKey || sourceRoleKey === row.roleKey) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const placement = e.clientY < midpoint ? "before" : "after";

    row.onReorderExtraRow?.({
      sourceRoleKey,
      targetRoleKey: row.roleKey,
      placement,
    });
  }

  return (
    <div
      draggable={row.isExtraSlot && !readOnly}
      onDragStart={handleRowDragStart}
      onDragOver={(e) => {
        if (isReorderDrag(e)) {
          e.preventDefault();
          return;
        }

        if (readOnly || row.blocked) return;
        e.preventDefault();
      }}
      onDrop={(e) => {
        if (isReorderDrag(e)) {
          e.preventDefault();
          handleRowDrop(e);
          return;
        }

        if (readOnly || row.blocked) return;
        e.preventDefault();
        row.onDropAssignment?.(e);
      }}
      onDragEnter={() => {
        if (!readOnly && !row.blocked) row.onDragEnter?.();
      }}
      onDragLeave={() => {
        if (!readOnly && !row.blocked) row.onDragLeave?.();
      }}
      style={{
        display: "grid",
        gap: 4,
        paddingTop: 6,
        borderTop: "1px solid #dbe7e3",
        cursor: row.isExtraSlot && !readOnly ? "grab" : "default",
      }}
    >
      {row.blocked ? (
        <button
          type="button"
          onClick={() => {
            if (!readOnly) row.onOpenAssign?.();
          }}
          disabled={readOnly}
          style={{
            fontSize: 11,
            color: "#64748b",
            background: "#f1f5f9",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
            padding: "6px 8px",
            fontWeight: 700,
            textAlign: "left",
            cursor: readOnly ? "default" : "pointer",
          }}
        >
          Blocked: {row.displayLabel || row.roleLabel}
        </button>
      ) : row.assignments?.length > 0 ? (
        <div style={{ display: "grid", gap: 6 }}>
          {sortAssignments(row.assignments).map((assignment) => (
            <AssignmentChip
              key={assignment.assignmentId}
              assignment={assignment}
              employee={employeeById.get(assignment.employeeId)}
              onOpen={() => row.onEditAssignment?.(assignment)}
              onDragStart={row.onDragStart}
              onDragEnd={row.onDragEnd}
            />
          ))}
        </div>
      ) : row.publishedShifts?.length > 0 ? (
        <div style={{ display: "grid", gap: 4 }}>
          {row.publishedShifts.map((shift) => (
            <button
              key={shift.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!readOnly) row.onPublishedShiftClick?.(shift);
              }}
              style={{
                fontSize: 11,
                color: "#166534",
                background: dragOver ? "#d1fae5" : "#ecfdf5",
                border: dragOver ? "2px solid #2563eb" : "1px solid #bbf7d0",
                borderRadius: 8,
                padding: "6px 8px",
                fontWeight: 700,
                textAlign: "left",
                cursor: readOnly ? "default" : "pointer",
              }}
            >
              Available: {shift.position || row.displayLabel || row.roleLabel}
              {shift.start && shift.end ? ` • ${shift.start}-${shift.end}` : ""}
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={row.onOpenAssign}
          disabled={readOnly}
          style={{
            fontSize: 11,
            color: "#166534",
            background: dragOver ? "#d1fae5" : "#ecfdf5",
            border: dragOver ? "2px solid #2563eb" : "1px solid #bbf7d0",
            borderRadius: 8,
            padding: "6px 8px",
            fontWeight: 700,
            textAlign: "left",
            cursor: readOnly ? "default" : "pointer",
          }}
        >
          Available: {row.displayLabel || row.roleLabel}
        </button>
      )}
    </div>
  );
}

export default function AssignmentSectionCard({
  iso,
  employeeById,
  dragOverKey,
  readOnly,
  groups,
  rowDragType,
}) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {groups.map((group) => (
        <div
          key={group.roleKey}
          className="schedule-slot-card"
          style={{
            border: "1px solid #d8e1ec",
            borderRadius: 10,
            background: "#ffffff",
            padding: 8,
            display: "grid",
            gap: 8,
          }}
        >
          <div
            style={{
              fontWeight: 900,
              fontSize: 10,
              lineHeight: 1.1,
              color: "#0f172a",
            }}
          >
            {group.displayLabel || group.roleLabel}
          </div>

          <div style={{ display: "grid", gap: 0 }}>
            {group.rows.map((row) => (
              <SectionRow
                key={row.roleKey}
                iso={iso}
                row={row}
                employeeById={employeeById}
                readOnly={readOnly}
                dragOverKey={dragOverKey}
                rowDragType={rowDragType}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}